import {
  DigitalAsset,
  DigitalAssetDTO,
  DigitalAssetEditDTO,
  DigitalAssetListRequestFilter,
  DigitalAssetListRequestSorting,
  DigitalAssetMetaData,
  DigitalAssetStatusUpdateRequest,
  DigitalAssetDeleteRequest,
  DigitalAssetStatusUpdateAction,
} from 'modules/domain/digitalAsset/types'
import { apiClient, DownloadParam } from 'modules/utils/httpClient'
import { endpoints } from 'modules/endpoints'
import { ListResponse } from 'types/api'
import { AxiosResponse } from 'axios'
import { UploadCompanyType } from 'types/entities'

export const getDigitalAssetsList = (
  uploadCompanyType: UploadCompanyType,
  filter: DigitalAssetListRequestFilter,
  sorting: DigitalAssetListRequestSorting,
  page: number,
  pageSize?: number,
) => {
  try {
    const finalFilter = {
      ...filter,
      uploaded_by_company_type: uploadCompanyType,
    }
    return apiClient.get<ListResponse<DigitalAsset>>(endpoints.digitalAssets(), {
      ...finalFilter,
      ...sorting,
      page: page,
      page_size: pageSize,
    })
  } catch (err) {
    console.error('DigitalAssets list fetch error!', err)
    throw err
  }
}

export const getDigitalAsset = (id: string) => {
  try {
    return apiClient.get<DigitalAsset>(endpoints.digitalAssets(id))
  } catch (err) {
    console.error('DigitalAsset fetch error!', err)
    throw err
  }
}

export const addDigitalAsset = (digitalAsset: DigitalAssetDTO) => {
  return apiClient.post<DigitalAsset>(endpoints.digitalAssets(), digitalAsset)
}

export const uploadDigitalAssetFile = (digitalAssetId: string, file: File) => {
  const formData = new FormData()
  formData.append('file_data', file)
  return apiClient.post<DigitalAsset>(endpoints.digitalAssetsUpload(digitalAssetId), formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export const downloadDigitalAssetFile = async (digitalAssetId: string, documentName?: string) => {
  const response: AxiosResponse = await apiClient.get(
    endpoints.digitalAssetsDownload(digitalAssetId),
    {},
    {
      responseType: 'blob',
      download: DownloadParam.No,
    },
    true,
  )
  if (window || document) {
    downloadFile(response, documentName)
  }
  return response.data
}

export const updateDigitalAsset = (id: string, digitalAsset: Partial<DigitalAssetEditDTO>) => {
  return apiClient.put<DigitalAsset>(endpoints.digitalAssets(id), digitalAsset)
}

export const softDeleteDigitalAssets = (digitalAssetIds: string[]) => {
  const request: DigitalAssetStatusUpdateRequest = {
    action: DigitalAssetStatusUpdateAction.soft_delete,
    digital_asset_ids: digitalAssetIds,
  }
  return apiClient.patch(endpoints.digitalAssets(), request)
}

export const restoreDigitalAssets = (digitalAssetIds: string[]) => {
  const request: DigitalAssetStatusUpdateRequest = {
    action: DigitalAssetStatusUpdateAction.restore,
    digital_asset_ids: digitalAssetIds,
  }
  return apiClient.patch(endpoints.digitalAssets(), request)
}

export const permanentDeleteDigitalAssets = (digitalAssetIds: string[]) => {
  const request: DigitalAssetDeleteRequest = {
    digital_asset_ids: digitalAssetIds,
  }
  return apiClient.delete(endpoints.digitalAssets(), request)
}

export const digitalAssetsCount = async (filter: DigitalAssetListRequestFilter = {}) => {
  const response = await apiClient.get<ListResponse<DigitalAsset>>(endpoints.digitalAssets(), {
    ...filter,
    page_size: 1,
  })
  return response.total_count
}

export const addLinkAsset = (linkAsset: DigitalAssetDTO) => {
  return apiClient.post<DigitalAsset>(endpoints.linkAsset(), linkAsset)
}

export const getDigitalAssetMetadata = async (id: string) => {
  try {
    return apiClient.get<DigitalAssetMetaData>(endpoints.digitalAssetMetaData(id))
  } catch (err) {
    console.error('Digital Asset metadata fetch error!', err)
    throw err
  }
}

//Probably generic functions
const convertContentDispositionHeaderToFileName = (contentDispositionHeader: string) => {
  //in case filename has special characters then it is preferable to keep them
  const utf8FileName = contentDispositionHeader.match(/filename\*=(?:UTF-8'')?([^;]+)/)
  if (utf8FileName) {
    return decodeURIComponent(utf8FileName[1])
  }

  const filename = contentDispositionHeader.match(/filename="(.*?)"/)
  if (filename) {
    return filename[1]
  }
  return null
}

const downloadFile = (response: AxiosResponse, documentName?: string) => {
  //in case none of the values are provided for filename then a default name will be used
  const defaultName = 'file'
  const fileName =
    convertContentDispositionHeaderToFileName(response.headers['content-disposition']) || documentName || defaultName
  const mimeType = response.headers['content-type']
  const link = document.createElement('a')

  const url = window.URL.createObjectURL(response.data)
  link.href = url
  link.setAttribute('download', fileName)
  link.setAttribute('type', mimeType)
  link.click()
  window.URL.revokeObjectURL(url)
}
